import { Shield, Play } from 'lucide-react';
import type { Product } from './types';

export const qualityAssuranceSystem: Product = {
  id: 'quality-assurance-system',
  title: 'Quality Assurance System',
  subtitle: 'AI-Powered Quality Management, Automated Evaluation & Conversation Intelligence',
  description: 'Enterprise-grade quality assurance platform designed specifically for contact center and customer service operations. Combines AI-powered 100% interaction monitoring, automated scoring, real-time speech analytics, and comprehensive compliance management to transform quality control from reactive sampling to proactive intelligence.',
  icon: Shield,
  image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  category: 'Operations & Compliance',
  status: 'Live',
  businessTypes: ['#contact-center', '#customer-service', '#quality-assurance', '#compliance', '#operations'],
  features: [
    // Core Quality Monitoring (8 features)
    'AI-Powered 100% Interaction Monitoring Across All Channels',
    'Real-Time Speech Analytics with Sentiment Detection',
    'Automated Quality Scoring with Customizable Criteria',
    'Smart Issue Detection & Root Cause Analysis',
    'Predictive Trend Analysis for Proactive Quality Management',
    'Multi-Channel Coverage (Voice, Chat, Email, Social)',
    'Role-Based Quality Dashboards & KPI Tracking',
    'Configurable Evaluation Templates & Scorecards',
    
    // Agent Performance & Coaching (8 features)
    'Automated Agent Performance Tracking & Benchmarking',
    'AI-Generated Coaching Recommendations',
    'Real-Time Performance Alerts & Notifications',
    'Skill Gap Analysis with Training Integration',
    'Agent Self-Assessment & Improvement Tools',
    'Calibration Session Management for Evaluators',
    'Performance Gamification & Recognition System',
    'Individual Development Plan Automation',
    
    // Compliance & Audit (7 features)
    'Automated Compliance Verification & Monitoring',
    'Regulatory Framework Management (GDPR, PCI-DSS, HIPAA)',
    'Complete Audit Trail & Access Logging',
    'Compliance Risk Scoring & Early Warning System',
    'Automated Report Generation for Auditors',
    'Policy Violation Detection & Escalation',
    'Documentation & Evidence Management',
    
    // Conversation Intelligence (7 features)
    'AI Transcription with 99% Accuracy',
    'Keyword & Phrase Detection Across Conversations',
    'Customer Sentiment Analysis & Emotion Detection',
    'Talk/Listen Ratio Analysis for Agents',
    'Silence Detection & Dead Air Monitoring',
    'Script Adherence Tracking & Verification',
    'Conversation Flow & Topic Mapping',
    
    // Advanced Analytics (8 features)
    'Real-Time Quality Metrics Dashboard',
    'Customer Satisfaction Correlation Analysis',
    'First Contact Resolution (FCR) Tracking',
    'Average Handle Time (AHT) Optimization',
    'Quality Trend Analysis & Forecasting',
    'Team & Department Benchmarking',
    'Custom Report Builder with Scheduling',
    'Business Intelligence Integration (BI Tools)',
    
    // Integration & Automation (6 features)
    'CCaaS Platform Integration (Genesys, Five9, NICE, etc.)',
    'CRM System Connectivity (Salesforce, HubSpot)',
    'Workforce Management (WFM) Sync',
    'Learning Management System (LMS) Integration',
    'Ticketing System Connection (Zendesk, ServiceNow, Jira)',
    'API-First Architecture with 200+ Pre-Built Connectors',
    
    // Enterprise Security (8 features)
    'End-to-End Data Encryption (AES-256)',
    'SOC 2 Type II & ISO 27001 Certified Infrastructure',
    'Role-Based Access Control (RBAC) with SSO',
    'Data Residency Options for Regional Compliance',
    'PCI-DSS Compliant Call Recording Storage',
    'GDPR & CCPA Data Privacy Compliance',
    'Multi-Factor Authentication (MFA)',
    'Secure Cloud Infrastructure with 99.99% Uptime'
  ],
  pricing: { setup: 52000, monthly: 2392 },
  color: 'from-emerald-600 to-teal-700',
  demoConfig: {
    type: 'interactive',
    url: '/quality-assurance/demo',
    title: 'Experience AI-Powered Quality Assurance',
    description: 'Explore our comprehensive QA platform with real-time monitoring, automated scoring, and conversation intelligence',
    buttonText: 'Launch QA Demo',
    icon: Play,
    buttonColor: 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700'
  }
};
